import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { FunctionComponent } from 'react';

import { Tooltip } from '../../../../components/Tooltip';
import { CreateMilestoneDrawer } from '../CreateMilestoneDrawer/CreateMilestoneDrawer';
import { DeleteMilestoneDialog } from '../DeleteMilestoneDialog/DeleteMilestoneDialog';
import { EditMilestoneDrawer } from '../EditMilestoneDrawer/EditMilestoneDrawer';
import { useMilestonesDrawer } from './useMilestonesDrawer';

interface MilestonesDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string | null;
}

export const MilestonesDrawer: FunctionComponent<MilestonesDrawerProps> = ({
  isOpen,
  onClose,
  eventId,
}) => {
  const {
    milestones,
    onCreateMilestone,
    isCreateMilestoneDrawerOpen,
    onCreateMilestoneDrawerClose,
    loading,
    onDeleteMilestone,
    onShowDeleteDialog,
    onCloseDeleteDialog,
    milestoneToDeleteId,
    event,
    editingMilestone,
    onEditMilestoneDrawerClose,
    onShowEditDialog,
  } = useMilestonesDrawer(eventId);

  return (
    <>
      <Drawer anchor="right" open={isOpen} onClose={onClose}>
        <Box sx={{ mt: 10, mx: 2, width: 360 }}>
          <Typography variant="h4">{"Paliers de l'événement"}</Typography>
          <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
            {milestones.map((milestone) => (
              <div key={milestone.id}>
                <ListItem
                  alignItems="flex-start"
                  secondaryAction={
                    <>
                      <Tooltip title="Modifier le palier">
                        <IconButton
                          color="primary"
                          onClick={() => onShowEditDialog(milestone)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer le palier">
                        <IconButton
                          color="error"
                          onClick={() => onShowDeleteDialog(milestone.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  }
                >
                  <ListItemText
                    primary={milestone.name}
                    secondary={
                      <Box flexDirection="column" display="flex">
                        {milestone.prices.map((price) => (
                          <Typography
                            key={price.day}
                            component="span"
                            variant="overline"
                            sx={{ color: 'text.secondary' }}
                          >
                            {price.day + 1} soir{price.day + 1 === 1 ? '' : 's'}{' '}
                            : {price.value / 100}€
                          </Typography>
                        ))}
                        {(milestone.noAlcoholDiscount ?? 0) > 0 ? (
                          <Typography
                            component="span"
                            variant="overline"
                            sx={{ color: 'text.secondary' }}
                          >
                            Réduction BT : -
                            {(milestone.noAlcoholDiscount ?? 0) / 100}€
                          </Typography>
                        ) : null}
                        {(milestone.bedroomValue ?? 0) > 0 ? (
                          <Typography
                            component="span"
                            variant="overline"
                            sx={{ color: 'text.secondary' }}
                          >
                            {`Prix d'une Chambre : ${(milestone.bedroomValue ?? 0) / 100}€`}
                          </Typography>
                        ) : null}
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ color: 'text.primary', display: 'inline' }}
                        >
                          Du {format(milestone.startAt, 'dd/MM/yyyy HH:mm')} au{' '}
                          {format(milestone.endAt, 'dd/MM/yyyy HH:mm')}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                <Divider component="li" />
              </div>
            ))}
            <Box display="flex" justifyContent="center" marginBottom={1}>
              {loading ? <CircularProgress /> : null}
            </Box>
          </List>
          <Button variant="outlined" fullWidth onClick={onCreateMilestone}>
            Ajouter un palier
          </Button>
        </Box>
      </Drawer>
      {isCreateMilestoneDrawerOpen && event ? (
        <CreateMilestoneDrawer
          event={event}
          isOpen={isCreateMilestoneDrawerOpen}
          onClose={onCreateMilestoneDrawerClose}
          otherMilestones={milestones}
        />
      ) : null}

      {editingMilestone !== null && event != null ? (
        <EditMilestoneDrawer
          event={event}
          isOpen={editingMilestone !== null}
          onClose={onEditMilestoneDrawerClose}
          milestone={editingMilestone}
        />
      ) : null}

      <DeleteMilestoneDialog
        onCancel={onCloseDeleteDialog}
        onDelete={onDeleteMilestone}
        open={milestoneToDeleteId !== null}
      />
    </>
  );
};
